import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { PageSection } from "@/components/AppLayout.jsx";
import { GsdReadableBlock, useI18n } from "@/coquille-bridge";
import { useAuth } from "@/context/AuthContext.jsx";
import { getDefaultRouteForRole } from "@/data/dojoOsOrgModel.js";
import { ensureKarateR0Session } from "@/api/karateR0SessionBridge.js";
import {
  formationGetProgress,
  formationGetTrackProgress,
  formationListTracks,
} from "@/api/karateR0FormationApi.js";
import { pickFormationText } from "@/formation/karateFormationLocale.js";
import {
  getWelcomeHomeCopy,
  getWelcomePathPreview,
  hasSeenWelcomeHome,
  markWelcomeHomeSeen,
} from "@/pages/connected/welcomeHomeContent.js";
import "@/pages/connected/welcome-home.css";

function isLiveFormationRole(role) {
  return role === "hanshi" || role === "admin" || role === "sensei";
}

function dashboardForRole(role) {
  if (role === "sensei") return "/sensei";
  if (role === "assistant") return "/sempai";
  if (role === "parent") return "/parent";
  if (role === "student") return "/mon-espace";
  return "/admin";
}

export function WelcomeHomePage() {
  const { isAuthenticated, role, activeRoleUi, username, authSource } = useAuth();
  const { lang } = useI18n();
  const locale = lang === "en" ? "en" : "fr";
  const navigate = useNavigate();
  const shellRole = activeRoleUi || role || "hanshi";
  const copy = getWelcomeHomeCopy(locale, shellRole);
  const liveFormation = isAuthenticated && isLiveFormationRole(shellRole);

  const [firstVisit, setFirstVisit] = useState(() => !hasSeenWelcomeHome(username));
  const [tracks, setTracks] = useState([]);
  const [progressList, setProgressList] = useState([]);
  const [activeDetail, setActiveDetail] = useState(null);
  const [loadState, setLoadState] = useState(liveFormation ? "loading" : "static");

  const reloadFormation = useCallback(async () => {
    if (!liveFormation) {
      setLoadState("static");
      return;
    }
    setLoadState("loading");
    const session = await ensureKarateR0Session({ auth: { role: shellRole, username, authSource } });
    if (!session.ok) {
      setLoadState("error");
      return;
    }
    const [tracksRes, progressRes] = await Promise.all([formationListTracks(), formationGetProgress()]);
    if (!tracksRes.response.ok) {
      setLoadState("error");
      return;
    }
    const trackList = tracksRes.body.tracks ?? [];
    setTracks(trackList);
    setProgressList(progressRes.body?.progress ?? []);
    const trackId = trackList[0]?.id;
    if (trackId) {
      const detail = await formationGetTrackProgress(trackId);
      if (detail.response.ok) setActiveDetail(detail.body);
    }
    setLoadState("ready");
  }, [liveFormation, shellRole, username, authSource]);

  useEffect(() => {
    reloadFormation();
  }, [reloadFormation]);

  const primaryTrack = tracks[0];
  const primaryProgress = progressList.find((p) => p.trackId === primaryTrack?.id) ?? progressList[0];
  const doneCount = primaryProgress?.doneCount ?? 0;
  const totalSteps = primaryProgress?.totalSteps ?? primaryTrack?.totalSteps ?? (shellRole === "sensei" ? 8 : 10);
  const percent = primaryProgress?.percent ?? 0;
  const completed = Boolean(primaryProgress?.completed) || (totalSteps > 0 && doneCount >= totalSteps);
  const started = doneCount > 0 || Boolean(primaryProgress?.activeStepId);
  const lastStep = activeDetail?.track?.steps?.find((s) => s.id === primaryProgress?.activeStepId);
  const pathPreview = getWelcomePathPreview(primaryTrack?.id || (shellRole === "sensei" ? "sensei" : "admin"), locale);
  const stepsBadge =
    shellRole === "sensei" || primaryTrack?.id === "sensei" ? copy.stepsLabelSensei : copy.stepsLabelAdmin;

  const markSeen = () => {
    markWelcomeHomeSeen(username);
    setFirstVisit(false);
  };

  const goFormation = (opts = {}) => {
    markSeen();
    const params = new URLSearchParams();
    if (opts.review) params.set("intro", "review");
    if (opts.start && primaryTrack?.id) {
      const step = primaryProgress?.activeStepId || activeDetail?.track?.steps?.[0]?.id;
      if (step) {
        params.set("track", primaryTrack.id);
        params.set("step", step);
      }
    }
    const q = params.toString();
    navigate(q ? `/formation?${q}` : "/formation");
  };

  const explore = () => {
    markSeen();
    navigate(dashboardForRole(shellRole));
  };

  if (!isAuthenticated) {
    return <Navigate to="/connexion" replace />;
  }

  return (
    <PageSection title={copy.pageTitle} testId="page-accueil-connecte">
      <div className="welcome-home" data-testid="welcome-home">
        <GsdReadableBlock className="welcome-home__hero" testId="welcome-home-hero">
          <h1 className="welcome-home__title">{copy.welcomeTitle}</h1>
          <p className="welcome-home__lead">{copy.welcomeBody}</p>
          <p className="welcome-home__role" data-testid="welcome-home-role-intro">
            {copy.roleIntro}
          </p>
        </GsdReadableBlock>

        {firstVisit ? (
          <GsdReadableBlock className="welcome-home__first-visit" testId="welcome-first-visit">
            <p className="welcome-home__badge">{copy.firstVisitBadge}</p>
            <p>{copy.firstVisitBody}</p>
            <div className="welcome-home__actions">
              {liveFormation ? (
                <button type="button" className="btn-primary" data-testid="welcome-start-training" onClick={() => goFormation()}>
                  {copy.startTraining}
                </button>
              ) : null}
              <button type="button" className="btn-secondary" data-testid="welcome-explore-app" onClick={explore}>
                {copy.exploreApp}
              </button>
            </div>
          </GsdReadableBlock>
        ) : null}

        {!firstVisit && liveFormation && started && !completed ? (
          <GsdReadableBlock className="welcome-home__resume" testId="welcome-resume-training">
            <h2>{copy.resumeTitle}</h2>
            <p data-testid="welcome-progress-summary">{copy.progressLabel(doneCount, totalSteps)}</p>
            {lastStep ? (
              <p data-testid="welcome-last-step">
                {copy.lastStepLabel} : {pickFormationText(lastStep.title, locale)}
              </p>
            ) : null}
            <button type="button" className="btn-primary" data-testid="welcome-continue-training" onClick={() => goFormation({ start: true })}>
              {copy.continueTraining}
            </button>
          </GsdReadableBlock>
        ) : null}

        {!firstVisit && liveFormation && completed ? (
          <GsdReadableBlock className="welcome-home__completed" testId="welcome-training-completed">
            <h2>{copy.completedTitle}</h2>
            <button type="button" className="btn-secondary" data-testid="welcome-review-training" onClick={() => goFormation({ review: true })}>
              {copy.reviewTraining}
            </button>
          </GsdReadableBlock>
        ) : null}

        {liveFormation ? (
          <GsdReadableBlock className="welcome-home__learn-card" testId="welcome-learn-card">
            <p className="welcome-home__learn-kicker">🎓 {copy.learnCardTitle}</p>
            <p className="welcome-home__learn-meta" data-testid="welcome-learn-steps-badge">
              {stepsBadge}
            </p>
            <p>{copy.learnCardBody}</p>
            <p className="welcome-home__progress-line" data-testid="welcome-learn-progress">
              {loadState === "ready" ? copy.progressLabel(doneCount, totalSteps) : copy.progressLabel(0, totalSteps)}
            </p>
            <div className="welcome-home__progress-bar" aria-hidden="true">
              <span style={{ width: `${percent}%` }} />
            </div>
            <div className="welcome-home__actions">
              <button
                type="button"
                className="btn-primary"
                data-testid="welcome-learn-cta"
                onClick={() => goFormation(started ? { start: true } : {})}
              >
                {started && !completed ? copy.continueTraining : copy.startTraining}
              </button>
              <Link to="/formation" className="welcome-home__link" data-testid="welcome-view-full-training" onClick={markSeen}>
                {copy.viewFullTraining}
              </Link>
            </div>
          </GsdReadableBlock>
        ) : (
          <GsdReadableBlock className="welcome-home__learn-card" testId="welcome-learn-card-soon">
            <p>{copy.noLiveTraining}</p>
            <button type="button" className="btn-primary" data-testid="welcome-open-dashboard" onClick={explore}>
              {copy.openDashboard}
            </button>
          </GsdReadableBlock>
        )}

        {!firstVisit ? null : (
          <GsdReadableBlock className="welcome-home__first-time-copy" testId="welcome-first-time-copy">
            <h2>{copy.firstTimeTitle}</h2>
            <p>{copy.firstTimeBody}</p>
          </GsdReadableBlock>
        )}

        <GsdReadableBlock className="welcome-home__how" testId="welcome-how-learn">
          <h2>{copy.howTitle}</h2>
          <ol className="welcome-home__how-list">
            {copy.howSteps.map((step) => (
              <li key={step.n}>
                <strong>
                  {step.n} — {step.title}
                </strong>
                <span>{step.body}</span>
              </li>
            ))}
          </ol>
        </GsdReadableBlock>

        {liveFormation ? (
          <GsdReadableBlock className="welcome-home__path" testId="welcome-path-preview">
            <h2>{copy.pathTitle}</h2>
            <ol className="welcome-home__path-list">
              {pathPreview.map((label, idx) => (
                <li key={`${idx}-${label}`}>
                  <span className="welcome-home__path-n">{idx + 1}</span>
                  <span>{label}</span>
                </li>
              ))}
            </ol>
            <Link to="/formation" className="welcome-home__link" data-testid="welcome-path-full-link" onClick={markSeen}>
              {copy.viewFullTraining}
            </Link>
          </GsdReadableBlock>
        ) : null}
      </div>
    </PageSection>
  );
}

/** @deprecated use getDefaultRouteForRole — kept for clarity in BT */
export function getWelcomeDefaultRoute(role) {
  return getDefaultRouteForRole(role);
}
